import { useCallback, useState } from 'react'
import { Outlet, useLocation } from 'react-router'
import { useRole } from '@/auth/useRole'
import { SparkyButton } from './SparkyButton'
import { SparkyCopilotSheet } from './SparkyCopilotSheet'
import { sparkyContextKey, useSparkyContext } from './useSparkyContext'

/**
 * Layout route that adds the global Sparky copilot to everything it wraps:
 * the floating button bottom-right plus the slide-over. Mounted INSIDE the
 * staff `RequireRole` boundary in `App.tsx` (spec-29 §8) — founders never
 * reach this element. The sparky backend is admin/team only, so the button is
 * additionally hidden for the fundraiser role (spec-54) instead of 403'ing.
 */
export function SparkyHost() {
  const [open, setOpen] = useState(false)
  const context = useSparkyContext()
  const { role } = useRole()
  const { pathname } = useLocation()
  // Last conversation per context key (session only, lives as long as the shell).
  const [lastByContext, setLastByContext] = useState<Record<string, string | null>>({})
  const key = sparkyContextKey(context)
  const remember = useCallback(
    (id: string | null) => setLastByContext((current) => ({ ...current, [key]: id })),
    [key],
  )
  // spec-64 U-042: no Sparky on the security page.
  const onSecurityPage = pathname === '/beveiliging' || pathname.startsWith('/beveiliging/')
  const sparkyAllowed = (role === 'admin' || role === 'team') && !onSecurityPage

  return (
    <>
      <Outlet />
      {sparkyAllowed ? (
        <>
          <SparkyButton onClick={() => setOpen(true)} />
          <SparkyCopilotSheet
            open={open}
            onOpenChange={setOpen}
            context={context}
            rememberedId={lastByContext[key] ?? null}
            onRemember={remember}
          />
        </>
      ) : null}
    </>
  )
}
